import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  SafText,
  SafTab,
  SafTabs,
  SafTabPanel,
  SafTable,
  SafButton,
  SafIcon,
  SafCombobox,
  SafOption,
  SafSelect,
  SafPagination,
  SafDialog,
  SafAlert,
  SafAnchor,
  SafCheckbox,
} from "@saffron/core-components/react";
import "../../../App.css";
import "../../../css/breadcrumb.css";
import "../../../css/common.css";
import {
  BackendTab,
  DevopsTab,
  CustomHeader,
  LucidChartImportTab,
  ComplianceResultsTab,
} from "../../../shared";
import { callMiddleware } from "../../../util/api";
import { ENVIRONMENTS, CLOUD_PROVIDERS, matchesTechStackProvider, isMajorCloudProviderValue } from "../../../util/constants";
import {
  Justification as JustificationComponent,
  TechnologyStackComparisonPage as TechnologyStackComparison,
} from "..";

const TechnologyStacks = () => {
  // Extract assetId from URL params
  const { assetId: urlAssetId } = useParams();
  const navigate = useNavigate();

  const [runtimeData, setRuntimeData] = useState([]);
  const [techDetailsMap, setTechDetailsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controlled, setControlled] = useState({
    totalItemCount: 0,
    currentPageIndex: 1,
    itemsArray: [5, 10, 25, 50, 100, 250, 500],
    itemsPerPage: 25,
  });
  const [filterValue, setFilterValue] = useState("Proposed");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [, setDeviations] = useState([]);
  const [selectedPatterns, setSelectedPatterns] = useState(new Set());
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  // Set initial selectedTab from localStorage or default to 'Frontend'
  const [selectedTab, setSelectedTab] = useState(() => {
    return localStorage.getItem("selectedTab") || "Frontend";
  });
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);
  const [openAIAlertOptions, setOpenAIAlertOptions] = useState(undefined);
  const [alertMessage, setAlertMessage] = useState("");
  const [aiAlertMessage, setAIAlertMessage] = useState("");

  // Cloud provider filter - reads from same sessionStorage as Cloud Permit
  const [selectedCloudProvider, setSelectedCloudProvider] = useState(() => {
    return (
      sessionStorage.getItem(`selectedCloudProvider_${urlAssetId}`) || ""
    );
  });

  const handleCloudProviderChange = (event) => {
    const provider = event.target.value;
    setSelectedCloudProvider(provider);
    if (provider) {
      sessionStorage.setItem(`selectedCloudProvider_${urlAssetId}`, provider);
      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage(`Cloud Provider set to ${provider} successfully!`);
      setTimeout(() => setOpenAlertOptions(undefined), 4000);
    } else {
      sessionStorage.removeItem(`selectedCloudProvider_${urlAssetId}`);
    }
  };

  const handlePatternCheckboxChange = (serviceId, patternRefId, isChecked) => {
    const key = `${serviceId}-${patternRefId}`;
    setSelectedPatterns((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const handleGlobalRemovePatternsClick = () => {
    if (selectedPatterns.size === 0) {
      setOpenAlertOptions(["toast", "warning"]);
      setAlertMessage("Please select at least one pattern to remove.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
      return;
    }
    setRemoveDialogOpen(true);
  };

  const handleOnClose = () => {
    setOpenAlertOptions(undefined);
    setOpenAIAlertOptions(undefined);
  };

  const confirmPatternRemoval = async () => {
    setRemoveDialogOpen(false);

    if (selectedPatterns.size === 0) {
      return;
    }

    try {
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      let currentTechStacks;
      let currentDeviations;

      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      const patternsByService = {};
      Array.from(selectedPatterns).forEach((key) => {
        const [serviceId, ...rest] = key.split("-");
        const patternRefId = rest.join("-");
        if (!patternsByService[serviceId]) {
          patternsByService[serviceId] = new Set();
        }
        patternsByService[serviceId].add(patternRefId);
      });

      const updatedTechStacks = currentTechStacks.map((item) => {
        if (
          (selectedTab === "Frontend" && item.tech_stack === "frontend") ||
          (selectedTab === "Backend" && item.tech_stack === "backend") ||
          (selectedTab === "DevOps" && item.tech_stack === "devops")
        ) {
          const updatedEnvironments = item.tech_stack_environment.map((env) => {
            if (env.services && Array.isArray(env.services)) {
              return {
                ...env,
                services: env.services.map((service) => {
                  const patternSet = patternsByService[service.service_id];
                  if (!patternSet || !service.pattern_ref_id) {
                    return service;
                  }

                  const filteredPatterns = (
                    service.pattern_ref_id || []
                  ).filter((refId) => !patternSet.has(refId));

                  return { ...service, pattern_ref_id: filteredPatterns };
                }),
              };
            }
            return env;
          });

          return {
            ...item,
            tech_stack_environment: updatedEnvironments,
          };
        }
        return item;
      });

      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: updatedTechStacks,
        deviations: currentDeviations,
      };

      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });

      setRuntimeData(updatedTechStacks);
      setSelectedPatterns(new Set());

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Selected patterns removed successfully!");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    } catch (err) {
      console.error("Error removing patterns:", err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to remove patterns. Please try again.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    }
  };

  // Fetch technology stacks data for the current asset
  const fetchTechnologyStacksData = useCallback(async () => {
    if (!urlAssetId) {
      console.error("No asset ID available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const data = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      // Check if data exists and has the expected structure
      if (
        data &&
        data.technology_stacks &&
        Array.isArray(data.technology_stacks)
      ) {
        setRuntimeData(data.technology_stacks);
        setDeviations(data.deviations || []);
      } else {
        // If the middleware returns an array directly (first item is what we want)
        if (
          Array.isArray(data) &&
          data.length > 0 &&
          data[0].technology_stacks
        ) {
          setRuntimeData(data[0].technology_stacks);
          setDeviations(data[0].deviations || []);
        } else {
          console.warn("Unexpected data structure:", data);
          setRuntimeData([]);
          setDeviations([]);
        }
      }
    } catch (err) {
      console.error("Error fetching technology stacks data:", err);
      setError(
        "Failed to fetch technology stack data. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  }, [urlAssetId]);

  const invokeTechComparisonFunction = useCallback(
    async (assetId) => {
      let timeoutId;
      const controller = new AbortController();

      try {
        setOpenAIAlertOptions(["toast", "info"]);
        setAIAlertMessage(
          "Refreshing Mismatched Technologies in the background"
        );
        timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000);

        const response = await fetch(
          process.env.REACT_APP_AI_TECHCOMPARISION_ENDPOINT,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              asset_id: assetId,
            }),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          const errorJson = await response.json();
          if (errorJson && errorJson.message) {
            setOpenAIAlertOptions(["toast", "info"]);
            setAIAlertMessage(errorJson.message);
            setTimeout(() => setOpenAIAlertOptions(undefined), 3000);
            return;
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const result = await response.json();
        console.log("Tech comparison result:", result);

        setOpenAlertOptions(undefined);

        setTimeout(() => {
          if (result.status === "success") {
            setTimeout(() => {
              fetchTechnologyStacksData();
            }, 2000);
          } else {
            setOpenAIAlertOptions(["toast", "error"]);
            setAIAlertMessage(
              "Technology comparison failed. Please try again."
            );
          }
          setTimeout(() => setOpenAIAlertOptions(undefined), 5000);
        }, 100);
      } catch (error) {
        console.error("Error invoking tech comparison function:", error);
        setOpenAIAlertOptions(undefined);

        setTimeout(() => {
          // Handle 504 specifically
          if (
            error.message.includes("504") ||
            error.message.includes("gateway")
          ) {
            setOpenAIAlertOptions(["toast", "warning"]);
            setAIAlertMessage(
              "The request timed out due to gateway settings. However, the analysis is still running in the background and should complete in 7-10 minutes. Please refresh the page after a few minutes to see the results."
            );
          } else if (error.name === "AbortError") {
            setOpenAIAlertOptions(["toast", "warning"]);
            setAIAlertMessage(
              "Request exceeded 15 minutes. Please check back later."
            );
          } else {
            setOpenAIAlertOptions(["toast", "error"]);
            setAIAlertMessage(
              "Failed to run technology comparison. Please try again."
            );
          }
          setTimeout(() => setOpenAIAlertOptions(undefined), 10000);
        }, 100);
      } finally {
        clearTimeout(timeoutId);
      }
    },
    [fetchTechnologyStacksData]
  );

  useEffect(() => {
    const checkAndInvokeTechComparison = async () => {
      try {
        // Fetch the tech comparison document for the given asset ID
        const dbName = process.env.REACT_APP_DB_TARGET;
        const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
        const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'cloudquery_mapping'`;

        const response = await callMiddleware("get", {
          dbName,
          containerName,
          query,
          partitionKey: urlAssetId,
        });

        let shouldInvokeFunction = false;

        if (Array.isArray(response) && response.length > 0) {
          const document = response[0];

          if (document.last_modified) {
            const lastAnalysisDate = new Date(document.last_modified);
            const currentDate = new Date();

            // Calculate difference in milliseconds
            const timeDifference = currentDate - lastAnalysisDate;

            // Convert to hours (24 hours = 86400000 milliseconds)
            const hoursDifference = timeDifference / (1000 * 60 * 60);

            console.log(
              `Last analysis was ${hoursDifference.toFixed(2)} hours ago`
            );

            // Check if more than 24 hours have passed
            if (hoursDifference > 24) {
              shouldInvokeFunction = true;
              console.log(
                "More than 24 hours have passed. Invoking tech comparison function..."
              );
            } else {
              console.log("Less than 24 hours. Skipping function invocation.");
            }
          } else {
            // No timestamp found, invoke function
            shouldInvokeFunction = true;
            console.log(
              "No analysis timestamp found. Invoking tech comparison function..."
            );
          }
        } else {
          // Document doesn't exist, invoke function
          shouldInvokeFunction = true;
          console.log(
            "No tech comparison document found. Invoking tech comparison function..."
          );
        }

        // Invoke the Azure Function if needed
        if (shouldInvokeFunction) {
          await invokeTechComparisonFunction(urlAssetId);
        }
      } catch (error) {
        console.error("Error checking tech comparison timestamp:", error);
      }
    };

    // Call the check function when component mounts
    checkAndInvokeTechComparison();
  }, [urlAssetId, invokeTechComparisonFunction]);
  // Handle URL parameter and localStorage synchronization
  useEffect(() => {
    const fetchAssetNameById = async (id) => {
      if (!id) return;

      try {
        const dbName = process.env.REACT_APP_DB_SOURCE;
        const data = await callMiddleware("get", {
          dbName,
          containerName:
            process.env.REACT_APP_CONTAINER_ASSET_INSIGHT_DATASOURCE,
          query: `SELECT c.id, c.asset_name FROM c WHERE c.id = '${id}'`,
        });
        if (data && data.length > 0) {
          const essentialInfo = {
            id: data[0].id,
            asset_name: data[0].asset_name,
          };
          // Update both state and localStorage
          localStorage.setItem("assetInfo", JSON.stringify(essentialInfo));
        }
      } catch (error) {
        console.error("Error fetching asset name:", error);
      }
    };

    if (urlAssetId) {
      // Always fetch fresh data when URL changes
      fetchAssetNameById(urlAssetId);
    } else if (localStorage.getItem("assetInfo")) {
      // If no URL param but we have localStorage data, redirect
      const storedInfo = JSON.parse(localStorage.getItem("assetInfo") || "{}");
      if (storedInfo.id) {
        navigate(`/${storedInfo.id}/technology-stacks`, { replace: true });
      } else {
        // No valid data, go to home
        navigate("/home");
      }
    } else {
      // No data at all, go to home
      navigate("/home");
    }
  }, [urlAssetId, navigate]);

  // Fetch tech stack mapping data from source database
  const fetchTechStackDetails = async () => {
    try {
      const dbName = process.env.REACT_APP_DB_SOURCE;
      const containerId = "tech-stack-mapping";
      const query = "SELECT * FROM c";

      const resources = await callMiddleware("get", {
        dbName,
        containerName: containerId,
        query,
      });

      // Create ONE global pattern lookup for all services
      const globalPatternLookup = {};
      const techDetailsMap = {};

      if (Array.isArray(resources)) {
        resources.forEach((item) => {
          if (item.capabilities && Array.isArray(item.capabilities)) {
            item.capabilities.forEach((capability) => {
              if (capability.services && Array.isArray(capability.services)) {
                capability.services.forEach((service) => {
                  // Build the global pattern lookup using pattern_ref_id as key
                  if (
                    service.supportingPattern &&
                    Array.isArray(service.supportingPattern)
                  ) {
                    service.supportingPattern.forEach((pattern) => {
                      // CHANGED: Use pattern_ref_id instead of pattern_name as the key
                      globalPatternLookup[pattern.pattern_ref_id] = pattern;
                    });
                  }

                  // Store service details without individual pattern lookup
                  techDetailsMap[service.tech_radar_id] = {
                    categoryName: item.category || "",
                    technologyName: service.technology_name || "",
                    capability: capability.capability || "",
                    recommendation: service.recommendation || "",
                    provider: service.provider || "",
                    description: service.description || "",
                  };
                });
              }
            });
          }
        });
      }

      // Store both the tech details and the global pattern lookup
      setTechDetailsMap({
        ...techDetailsMap,
        _globalPatternLookup: globalPatternLookup,
      });
    } catch (error) {
      console.error("Error fetching tech stack details from middleware", error);
    }
  };

  useEffect(() => {
    // First fetch the technology stacks data
    fetchTechnologyStacksData();
    // Then fetch the tech stack details to map service IDs to their details
    fetchTechStackDetails();

    // Listen for refresh event from TechnologyStackComparison
    const handleRefresh = () => {
      fetchTechnologyStacksData();
    };
    window.addEventListener("refresh-tech-stack-table", handleRefresh);
    return () => {
      window.removeEventListener("refresh-tech-stack-table", handleRefresh);
    };
  }, [fetchTechnologyStacksData]);

  const filteredData = useMemo(() => {
    return runtimeData.filter((item) => {
      // Make sure tech_stack_environment exists and is an array
      if (
        !item.tech_stack_environment ||
        !Array.isArray(item.tech_stack_environment)
      ) {
        return false;
      }

      // Filter by selected tab
      const matchesTab =
        (selectedTab === "Frontend" &&
          (item.tech_stack === "frontend" || !item.tech_stack)) ||
        (selectedTab === "Backend" && item.tech_stack === "backend") ||
        (selectedTab === "DevOps" && item.tech_stack === "devops");

      if (!matchesTab) return false;

      // If no environment filter is applied, include all items that match the tab
      if (!filterValue || filterValue === "All") {
        // Apply cloud provider filter if selected
        if (selectedCloudProvider) {
          // Include rows that have at least one service matching the provider
          // OR one service that is provider-agnostic (not a recognised major cloud)
          return item.tech_stack_environment.some((env) =>
            env.services?.some((service) => {
              const techDetails = techDetailsMap[service.service_id];
              return (
                matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) ||
                !isMajorCloudProviderValue(techDetails?.provider)
              );
            })
          );
        }
        return true;
      }

      // Filter by environment AND cloud provider
      const hasMatchingEnvironment = item.tech_stack_environment.some(
        (env) => env.environment === filterValue
      );

      if (!hasMatchingEnvironment) return false;

      // Apply cloud provider filter if selected
      if (selectedCloudProvider) {
        const matchingEnv = item.tech_stack_environment.find(
          (env) => env.environment === filterValue
        );
        return matchingEnv?.services?.some((service) => {
          const techDetails = techDetailsMap[service.service_id];
          return (
            matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) ||
            !isMajorCloudProviderValue(techDetails?.provider)
          );
        });
      }

      return true;
    });
  }, [runtimeData, filterValue, selectedTab, selectedCloudProvider, techDetailsMap]);

  const paginatedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const allServices = [];

    // Extract the global pattern lookup once
    const globalPatternLookup = techDetailsMap._globalPatternLookup || {};

    filteredData.forEach((item) => {
      if (
        item.tech_stack_environment &&
        Array.isArray(item.tech_stack_environment)
      ) {
        const environments =
          !filterValue || filterValue === "All"
            ? item.tech_stack_environment
            : item.tech_stack_environment.filter(
                (env) => env.environment === filterValue
              );

        environments.forEach((env) => {
          // Handle new structure with services array
          if (env.services && Array.isArray(env.services)) {
            env.services.forEach((service) => {
              const techDetails = techDetailsMap[service.service_id];

              // Skip only if the service belongs to a different specific major cloud provider.
              // Provider-agnostic services (N/A, empty, non-major) are always shown.
              if (
                selectedCloudProvider &&
                !matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) &&
                isMajorCloudProviderValue(techDetails?.provider)
              ) {
                return;
              }
              
              if (techDetails) {
                // CHANGED: Use pattern_ref_id instead of pattern_names
                const fullPatterns = (service.pattern_ref_id || [])
                  .map((refId) => {
                    return globalPatternLookup[refId] || null;
                  })
                  .filter(Boolean);

                allServices.push({
                  ...techDetails,
                  environment: env.environment,
                  tech_radar_id: service.service_id,
                  // Use the reconstructed full pattern objects
                  supportingPattern: fullPatterns,
                });
              } else {
                // Unknown services have no specific provider (N/A), always include them
                allServices.push({
                  categoryName: "N/A",
                  technologyName:
                    service.service_name || `Service ID: ${service.service_id}`,
                  capability: "N/A",
                  recommendation: service.service_name
                    ? "Couldn't find in Tech Radar"
                    : "N/A",
                  provider: "N/A",
                  environment: env.environment,
                  tech_radar_id: service.service_id || service.service_name,
                  supportingPattern: [],
                });
              }
            });
          }
          // Handle old structure with service_ids array (backward compatibility)
          else if (env.service_ids && Array.isArray(env.service_ids)) {
            env.service_ids.forEach((serviceId) => {
              const techDetails = techDetailsMap[serviceId];
              if (
                selectedCloudProvider &&
                !matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) &&
                isMajorCloudProviderValue(techDetails?.provider)
              ) {
                return;
              }
              if (techDetails) {
                allServices.push({
                  ...techDetails,
                  environment: env.environment,
                  tech_radar_id: serviceId,
                  supportingPattern: techDetails.supportingPattern || [],
                });
              } else {
                allServices.push({
                  categoryName: "Unknown",
                  technologyName: `Service ID: ${serviceId}`,
                  capability: "Unknown",
                  recommendation: "Unknown",
                  provider: "N/A",
                  environment: env.environment,
                  tech_radar_id: serviceId,
                  supportingPattern: [],
                });
              }
            });
          }
        });
      }
    });

    const startIndex =
      (controlled.currentPageIndex - 1) * controlled.itemsPerPage;
    const endIndex = startIndex + controlled.itemsPerPage;

    // Apply sorting and pagination
    let sortedServices = [...allServices];

    if (sortField) {
      sortedServices.sort((a, b) => {
        let valueA, valueB;

        switch (sortField) {
          case "Category":
            valueA = a.categoryName || "";
            valueB = b.categoryName || "";
            break;
          case "Capability":
            valueA = a.capability || "";
            valueB = b.capability || "";
            break;
          case "TechnologyName":
            valueA = a.technologyName || "";
            valueB = b.technologyName || "";
            break;
          case "Recommendation":
            valueA = a.recommendation || "";
            valueB = b.recommendation || "";
            break;
          case "Provider":
            valueA = a.provider || "";
            valueB = b.provider || "";
            break;
          default:
            return 0;
        }

        if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
        if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    const paginatedServices = sortedServices.slice(startIndex, endIndex);
    return paginatedServices;
  }, [
    filteredData,
    controlled.currentPageIndex,
    controlled.itemsPerPage,
    techDetailsMap,
    sortField,
    sortOrder,
    filterValue,
    selectedCloudProvider,
  ]);
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const onPrevious = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.max(prev.currentPageIndex - 1, 1),
    }));
  };

  const onNext = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.min(
        prev.currentPageIndex + 1,
        Math.ceil(totalItems / controlled.itemsPerPage)
      ),
    }));
  };

  const onChange = (event) => {
    setControlled((prev) => ({ ...prev, currentPageIndex: event.detail }));
  };

  const onItemsPerPageChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      itemsPerPage: event.detail,
      currentPageIndex: 1,
    }));
  };

  const totalItems = useMemo(() => {
    const count = filteredData.reduce((total, item) => {
      return (
        total +
        item.tech_stack_environment.reduce((envCount, env) => {
          // Handle both new and old data structures
          if (env.services && Array.isArray(env.services)) {
            const servicesCount = selectedCloudProvider
              ? env.services.filter((service) => {
                  const techDetails = techDetailsMap[service.service_id];
                  return (
                    matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) ||
                    !isMajorCloudProviderValue(techDetails?.provider)
                  );
                }).length
              : env.services.length;
            return envCount + servicesCount;
          } else if (env.service_ids && Array.isArray(env.service_ids)) {
            const serviceIdsCount = selectedCloudProvider
              ? env.service_ids.filter((serviceId) => {
                  const techDetails = techDetailsMap[serviceId];
                  return (
                    matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) ||
                    !isMajorCloudProviderValue(techDetails?.provider)
                  );
                }).length
              : env.service_ids.length;
            return envCount + serviceIdsCount;
          }
          return envCount;
        }, 0)
      );
    }, 0);
    return count;
  }, [filteredData, techDetailsMap, selectedCloudProvider]);

  useEffect(() => {
    setControlled((prev) => ({
      ...prev,
      totalItemCount: totalItems,
    }));
  }, [totalItems]);

  const handleDeleteClick = (serviceId, environment) => {
    setServiceToDelete({ id: serviceId, environment });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
    if (!serviceToDelete) return;

    try {
      // First, get the latest data to ensure we're working with current state
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });
      let currentTechStacks, currentDeviations;

      // Handle different response formats
      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      // Update the data by removing the service
      const updatedTechStacks = currentTechStacks.map((item) => {
        // Only update the tech stack that matches the currently selected tab
        if (
          (selectedTab === "Frontend" && item.tech_stack === "frontend") ||
          (selectedTab === "Backend" && item.tech_stack === "backend") ||
          (selectedTab === "DevOps" && item.tech_stack === "devops")
        ) {
          const updatedEnvironments = item.tech_stack_environment.map((env) => {
            // Only update the environment where the service needs to be removed
            if (env.environment === serviceToDelete.environment) {
              // Handle new structure
              if (env.services) {
                return {
                  ...env,
                  services: env.services.filter((service) => {
                    // Check both service_id and service_name for identification
                    const serviceIdentifier =
                      service.service_id || service.service_name;
                    return serviceIdentifier !== serviceToDelete.id;
                  }),
                };
              }
              // Handle old structure (backward compatibility)
              if (env.service_ids) {
                return {
                  ...env,
                  service_ids: env.service_ids.filter(
                    (id) => id !== serviceToDelete.id
                  ),
                };
              }
            }
            return env;
          });

          return {
            ...item,
            tech_stack_environment: updatedEnvironments,
          };
        }
        // Return unchanged for other tech stacks
        return item;
      });

      // Update deviations - only keep deviations for service IDs/names that still exist in "Proposed" environment
      const proposedServiceIds = new Set();

      updatedTechStacks.forEach((stack) => {
        stack.tech_stack_environment.forEach((env) => {
          if (env.environment === "Proposed") {
            // Handle new structure
            if (env.services && Array.isArray(env.services)) {
              env.services.forEach((service) => {
                // Use service_id or service_name as identifier
                const serviceIdentifier =
                  service.service_id || service.service_name;
                const techDetails = techDetailsMap[service.service_id];
                if (techDetails) {
                  const recommendation = (
                    techDetails.recommendation || ""
                  ).toLowerCase();
                  if (
                    recommendation !== "adopt" &&
                    recommendation !== "mandate"
                  ) {
                    proposedServiceIds.add(serviceIdentifier);
                  }
                } else if (service.service_name) {
                  // For services without techDetails (service_name only), add them to track deviations
                  proposedServiceIds.add(service.service_name);
                }
              });
            }
            // Handle old structure
            else if (env.service_ids && Array.isArray(env.service_ids)) {
              env.service_ids.forEach((serviceId) => {
                const techDetails = techDetailsMap[serviceId];
                if (techDetails) {
                  const recommendation = (
                    techDetails.recommendation || ""
                  ).toLowerCase();
                  if (
                    recommendation !== "adopt" &&
                    recommendation !== "mandate"
                  ) {
                    proposedServiceIds.add(serviceId);
                  }
                }
              });
            }
          }
        });
      });

      // Filter deviations to only include those that should be in the deviations array
      const updatedDeviations = currentDeviations.filter((deviation) => {
        const deviationIdentifier =
          deviation.service_id || deviation.service_name;
        return proposedServiceIds.has(deviationIdentifier);
      });

      // Prepare the updated document
      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: updatedTechStacks,
        deviations: updatedDeviations,
      };

      // Update the database
      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });
      // Note: cloudquery_mapping document is not updated as it's a read-only data source

      // --- Update lucid_mcp_tech_summary: set present_in_tech_stack=false for this service ---
      try {
        const lucidQuery = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'lucid_mcp_tech_summary'`;
        const lucidDataResult = await callMiddleware("get", {
          dbName,
          containerName,
          query: lucidQuery,
          partitionKey: urlAssetId,
        });

        let lucidData;
        if (
          lucidDataResult &&
          Array.isArray(lucidDataResult) &&
          lucidDataResult.length > 0
        ) {
          lucidData = lucidDataResult[0];
        } else if (
          lucidDataResult &&
          lucidDataResult.id === "lucid_mcp_tech_summary"
        ) {
          lucidData = lucidDataResult;
        }

        if (
          lucidData &&
          lucidData.lucid_summary?.combined_summary?.technologies
        ) {
          let updated = false;
          lucidData.lucid_summary.combined_summary.technologies.forEach(
            (tech) => {
              const techName =
                typeof tech === "string"
                  ? tech
                  : tech.technology || tech.name || "";

              // Match by tech_radar_id or service_name
              const removedTechDetails = techDetailsMap[serviceToDelete.id];
              const isMatch =
                (removedTechDetails &&
                  techName.toLowerCase() ===
                    removedTechDetails.technologyName?.toLowerCase()) ||
                (serviceToDelete.id &&
                  techName
                    .toLowerCase()
                    .includes(serviceToDelete.id.toLowerCase())) ||
                techName.toLowerCase() ===
                  String(serviceToDelete.id).toLowerCase();

              if (
                isMatch &&
                typeof tech === "object" &&
                tech.present_in_tech_stack !== false
              ) {
                tech.present_in_tech_stack = false;
                updated = true;
              }
            }
          );

          if (updated) {
            lucidData.last_modified = new Date().toISOString();
            await callMiddleware("insert", {
              dbName,
              containerName,
              jsonData: lucidData,
              partitionKey: urlAssetId,
            });
            console.log("Updated lucid_mcp_tech_summary after removal");
          }
        }
      } catch (lucidErr) {
        console.error(
          "Error updating lucid_mcp_tech_summary after removal:",
          lucidErr
        );
      }

      // Update local state with the latest data
      setRuntimeData(updatedTechStacks);
      setDeviations(updatedDeviations);

      // Reset pagination to first page
      setControlled((prev) => ({
        ...prev,
        currentPageIndex: 1,
      }));
    } catch (err) {
      console.error("Error deleting service:", err);
      // You might want to show an error message to the user here
    } finally {
      setServiceToDelete(null);
    }
  };

  return (
    <div style={{ marginTop: "50px" }}>
      <CustomHeader
        headingText={"Technology Stacks"}
        assetId={urlAssetId}
        archVersion={"1.0"}
        headerDesc={
          "Tech stacks are a collection of technologies and building blocks utilized in building / developing an application. Stacks are broken down into groupings based on taxonomies which consist of patterns and individual building blocks. Patterns are grouped at 4 levels based on the completeness of an end to end solution."
        }
      />
      {/* Cloud Provider selector - positioned on the right, same as Cloud Permit */}
      <div
        style={{
          position: "fixed",
          top: "110px",
          right: "40px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          zIndex: 99,
        }}
      >
        <SafText>
          <strong>Cloud Provider:</strong>
        </SafText>
        <SafSelect
          value={selectedCloudProvider || ""}
          onChange={handleCloudProviderChange}
          style={{ width: "200px" }}
        >
          <SafOption value="">Select Cloud Provider</SafOption>
          {CLOUD_PROVIDERS.map((p) => (
            <SafOption key={p} value={p}>{p}</SafOption>
          ))}
        </SafSelect>
      </div>
      <br />
      <div style={{ paddingTop: "95px", position: "relative" }}>
        {loading ? (
          <div>Loading technology stack data...</div>
        ) : error ? (
          <div style={{ color: "red" }}>{error}</div>
        ) : (
          <>
            <SafTabs
            id="techStackTabs"
            orientation="horizontal"
            value={selectedTab}
            defaultValue={selectedTab}
            activeTab={selectedTab}
            activeid={selectedTab}
          >
            <SafTab
              id="Frontend"
              value="Frontend"
              onClick={() => {
                setSelectedTab("Frontend");
                localStorage.setItem("selectedTab", "Frontend");
              }}
              style={{
                borderBottom:
                  selectedTab === "Frontend" ? "3px solid #d64000" : "",
                fontWeight: selectedTab === "Frontend" ? "bold" : "",
                color: selectedTab === "Frontend" ? "#d64000" : "",
              }}
            >
              Front-end
            </SafTab>
            <SafTab
              id="Backend"
              defaultChecked={true}
              value="Backend"
              ariaSelected={selectedTab === "Backend"}
              onClick={() => {
                setSelectedTab("Backend");
                localStorage.setItem("selectedTab", "Backend");
              }}
              style={{
                borderBottom:
                  selectedTab === "Backend" ? "3px solid #d64000" : "",
                fontWeight: selectedTab === "Backend" ? "bold" : "",
                color: selectedTab === "Backend" ? "#d64000" : "",
              }}
            >
              Back-end
            </SafTab>
            <SafTab
              id="DevOps"
              value="DevOps"
              ariaSelected={selectedTab === "DevOps"}
              onClick={() => {
                setSelectedTab("DevOps");
                localStorage.setItem("selectedTab", "DevOps");
              }}
              style={{
                borderBottom:
                  selectedTab === "DevOps" ? "3px solid #d64000" : "",
                fontWeight: selectedTab === "DevOps" ? "bold" : "",
                color: selectedTab === "DevOps" ? "#d64000" : "",
              }}
            >
              Devops
            </SafTab>
            <SafTab
              id="LucidChartImport"
              value="LucidChartImport"
              ariaSelected={selectedTab === "LucidChartImport"}
              onClick={() => {
                setSelectedTab("LucidChartImport");
                localStorage.setItem("selectedTab", "LucidChartImport");
              }}
              style={{
                borderBottom:
                  selectedTab === "LucidChartImport" ? "3px solid #d64000" : "",
                fontWeight: selectedTab === "LucidChartImport" ? "bold" : "",
                color: selectedTab === "LucidChartImport" ? "#d64000" : "",
              }}
            >
              Lucid Chart Import
            </SafTab>
            <SafTab
              id="ComplianceResults"
              value="ComplianceResults"
              ariaSelected={selectedTab === "ComplianceResults"}
              onClick={() => {
                setSelectedTab("ComplianceResults");
                localStorage.setItem("selectedTab", "ComplianceResults");
              }}
              style={{
                borderBottom:
                  selectedTab === "ComplianceResults" ? "3px solid #d64000" : "",
                fontWeight: selectedTab === "ComplianceResults" ? "bold" : "",
                color: selectedTab === "ComplianceResults" ? "#d64000" : "",
              }}
            >
              Compliance Results
            </SafTab>
            <SafTabPanel>
              <div className="headers">
                {/* <h3>Front-end</h3>
                <div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                    }}
                  >
                    {visiblecards.map((card, index) => (
                      <SafCard
                        key={index}
                        style={{
                          width: "210px",
                          height: "200px",
                          position: "relative",
                        }}
                      >
                        <p>
                          <strong>{card.title}</strong>
                        </p>
                        <p>{card.percentage}</p>
                        {index === 3 && Data.length > 4 && (
                          <SafButton
                            onClick={() => setShowAll(!showAll)}
                            style={{
                              position: "absolute",
                              bottom: "10px",
                              right: "10px",
                            }}
                          >
                            {showAll ? "Show less" : "Show all"}
                          </SafButton>
                        )}
                      </SafCard>
                    ))}
                  </div>
                </div> */}
                <SafText>
                  <strong>Environment</strong>
                </SafText>
                &nbsp;
                <br />
                <div>
                  <div
                    style={{
                      maxWidth: "100%",
                      margin: "0",
                      marginTop: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <SafCombobox
                        id="combobox-environment"
                        name="environment-backend"
                        onChange={(event) => setFilterValue(event.target.value)}
                        value={filterValue}
                        style={{ width: "300px" }}
                        onFocus={(event) => {
                          event.target.value = ""; // Clear the input to show all options
                        }}
                        onBlur={(event) => {
                          if (!event.target.value) {
                            setFilterValue("Proposed"); // Reset to default if nothing selected
                          }
                        }}
                      >
                        {ENVIRONMENTS.map((env, index) => (
                          <SafOption key={index}>{env}</SafOption>
                        ))}
                      </SafCombobox>
                      <div>
                        <SafButton
                          appearance="secondary"
                          size="medium"
                          onClick={() => handleGlobalRemovePatternsClick()}
                          disabled={selectedPatterns.size === 0}
                          style={{ marginRight: "10px" }}
                        >
                          <SafIcon
                            slot="start"
                            icon-name="minus"
                            appearance="solid"
                            presentation
                          />
                          Remove Patterns ({selectedPatterns.size})
                        </SafButton>
                        {selectedCloudProvider ? (
                          <Link
                            to={`/${urlAssetId}/technology-stacks/Addtechstack`}
                            state={{ cloudProvider: selectedCloudProvider }}
                          >
                            <SafButton
                              appearance="secondary"
                              size="small"
                              id="button-dialog-trigger"
                              aria-haspopup="dialog"
                              aria-controls="button-dialog-overlay"
                            >
                              <SafIcon
                                slot="start"
                                icon-name="plus"
                                appearance="solid"
                                presentation
                              ></SafIcon>
                              Add
                            </SafButton>
                          </Link>
                        ) : (
                          <SafButton
                            appearance="secondary"
                            size="small"
                            id="button-dialog-trigger"
                            aria-haspopup="dialog"
                            aria-controls="button-dialog-overlay"
                            disabled={true}
                            title="Please select a cloud provider"
                          >
                            <SafIcon
                              slot="start"
                              icon-name="plus"
                              appearance="solid"
                              presentation
                            ></SafIcon>
                            Add
                          </SafButton>
                        )}
                      </div>
                      {/* <Link to="/technology-stacks/Addtechstack"> */}
                    </div>
                    <br />
                    <SafText>
                      Table shows the tech Stack for the selected asset and
                      environment
                    </SafText>
                    <br />

                    <div style={{ width: "100%", marginTop: "10px" }}>
                      <SafTable
                        alternating-rows="true"
                        density="inherit"
                        header-background="default"
                        inline-borders="true"
                      >
                        <thead>
                          <tr>
                            <th
                              onClick={() => handleSort("TechnologyName")}
                              style={{ width: "200px" }}
                            >
                              Technology Name
                              <SafIcon
                                iconName={
                                  sortField === "TechnologyName"
                                    ? sortOrder === "asc"
                                      ? "arrow-up"
                                      : "arrow-down"
                                    : "arrow-up-arrow-down"
                                }
                              />
                            </th>
                            <th
                              onClick={() => handleSort("Category")}
                              style={{ width: "200px" }}
                            >
                              Category
                              <SafIcon
                                iconName={
                                  sortField === "Category"
                                    ? sortOrder === "asc"
                                      ? "arrow-up"
                                      : "arrow-down"
                                    : "arrow-up-arrow-down"
                                }
                              />
                            </th>
                            <th
                              onClick={() => handleSort("Capability")}
                              style={{ width: "200px" }}
                            >
                              Capability
                              <SafIcon
                                iconName={
                                  sortField === "Capability"
                                    ? sortOrder === "asc"
                                      ? "arrow-up"
                                      : "arrow-down"
                                    : "arrow-up-arrow-down"
                                }
                              />
                            </th>
                            <th
                              onClick={() => handleSort("Supporting patterns")}
                              style={{ width: "200px" }}
                            >
                              Supporting Patterns
                            </th>
                            <th
                              onClick={() => handleSort("Recommendation")}
                              style={{ width: "200px" }}
                            >
                              Recommendation
                              <SafIcon
                                iconName={
                                  sortField === "Recommendation"
                                    ? sortOrder === "asc"
                                      ? "arrow-up"
                                      : "arrow-down"
                                    : "arrow-up-arrow-down"
                                }
                              />
                            </th>
                            <th
                              onClick={() => handleSort("compliance status")}
                              style={{ width: "200px" }}
                            >
                              Compliance Status
                            </th>
                            <th
                              onClick={() => handleSort("Provider")}
                              style={{ width: "150px" }}
                            >
                              Provider
                              <SafIcon
                                iconName={
                                  sortField === "Provider"
                                    ? sortOrder === "asc"
                                      ? "arrow-up"
                                      : "arrow-down"
                                    : "arrow-up-arrow-down"
                                }
                              />
                            </th>
                            <th
                              onClick={() => handleSort("Environment")}
                              style={{ width: "150px" }}
                            >
                              Environment
                            </th>
                            <th style={{ width: "150px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* {selectedTab === "Frontend" && */}
                          {paginatedData && paginatedData.length > 0 ? (
                            paginatedData.map((service, index) => {
                              return (
                                <tr key={index}>
                                  <td>{service?.technologyName || "N/A"}</td>
                                  <td>{service?.categoryName || "N/A"}</td>
                                  <td>{service?.capability || "N/A"}</td>
                                  {/* <td>{service?.supportingPattern || "N/A"}</td> */}
                                  <td>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "4px",
                                        }}
                                      >
                                        {Array.isArray(
                                          service?.supportingPattern
                                        ) &&
                                        service.supportingPattern.length > 0 ? (
                                          service.supportingPattern.map(
                                            (pattern) => (
                                              <div
                                                key={pattern.pattern_ref_id}
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                }}
                                              >
                                                <SafCheckbox
                                                  id={`pattern-${service.tech_radar_id}-${pattern.pattern_ref_id}`}
                                                  checked={selectedPatterns.has(
                                                    `${service.tech_radar_id}-${pattern.pattern_ref_id}`
                                                  )}
                                                  onChange={(e) =>
                                                    handlePatternCheckboxChange(
                                                      service.tech_radar_id,
                                                      pattern.pattern_ref_id,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                                <SafAnchor
                                                  href={`https://techtoc.thomsonreuters.com/${pattern.pattern_link}/`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{ fontSize: "14px" }}
                                                >
                                                  {pattern.pattern_name}
                                                </SafAnchor>
                                              </div>
                                            )
                                          )
                                        ) : (
                                          <span>N/A</span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td>{service?.recommendation || "N/A"}</td>
                                  <td>NA</td>
                                  <td>{service?.provider || "N/A"}</td>
                                  <td>{service?.environment || "N/A"}</td>
                                  <td>
                                    <SafIcon
                                      icon-name="trash-can"
                                      style={{
                                        cursor: "pointer",
                                        color: "#404040",
                                      }}
                                      onClick={() =>
                                        handleDeleteClick(
                                          service?.tech_radar_id,
                                          service?.environment
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="9" style={{ textAlign: "center" }}>
                                No frontend technology stack data available.
                                Click "Add" to add new technology stacks.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </SafTable>
                      {/* Move pagination here below the second table */}
                      {filteredData.length > 0 && (
                        <div style={{ width: "100%" }}>
                          <SafPagination
                            data-testid="controlled"
                            controlled={true}
                            totalItemCount={controlled.totalItemCount}
                            currentPageIndex={controlled.currentPageIndex}
                            itemsArray={controlled.itemsArray}
                            itemsPerPage={controlled.itemsPerPage}
                            onPrevious={onPrevious}
                            onNext={onNext}
                            onChange={onChange}
                            onItemsPerPageChange={onItemsPerPageChange}
                          />
                        </div>
                      )}
                    </div>
                    {/* --- Non-Adopt Frontend Technologies Table (inside same scrollable area as main table) --- */}
                    <JustificationComponent techStack={"frontend"} />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        margin: "16px 150px",
                      }}
                    >
                      {openAIAlertOptions && (
                        <SafAlert
                          appearance={openAIAlertOptions[1]}
                          alertType={openAIAlertOptions[0]}
                          iconAriaLabel="icon aria label"
                          closeAriaLabel="Close"
                          onClose={handleOnClose}
                          style={
                            openAIAlertOptions[1] === "info"
                              ? {
                                  position: "static",
                                  color: "#003366",
                                  opacity: 1,
                                }
                              : { position: "static" }
                          }
                        >
                          {aiAlertMessage}
                        </SafAlert>
                      )}
                    </div>
                    <div style={{ width: "100%", marginTop: "50px" }}>
                      <TechnologyStackComparison selectedCloudProvider={selectedCloudProvider} />
                    </div>
                  </div>
                </div>
              </div>
            </SafTabPanel>
            <SafTabPanel>
              <BackendTab selectedCloudProvider={selectedCloudProvider} />
            </SafTabPanel>
            <SafTabPanel>
              <DevopsTab selectedCloudProvider={selectedCloudProvider} />
            </SafTabPanel>
            <SafTabPanel>
              {selectedTab === "LucidChartImport" && <LucidChartImportTab />}
            </SafTabPanel>
            <SafTabPanel>
              {selectedTab === "ComplianceResults" && (
                <ComplianceResultsTab
                  selectedCloudProvider={selectedCloudProvider}
                />
              )}
            </SafTabPanel>
          </SafTabs>
          </>
        )}
      </div>

      <SafDialog
        modal={false}
        is-alert={false}
        is-header={true}
        is-footer={true}
        no-focus-trap={false}
        dialog-title="Do you want to delete this item?"
        hidden={!deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <SafButton appearance="secondary" onClick={confirmDelete}>
            Yes
          </SafButton>
          <SafButton
            appearance="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            No
          </SafButton>
        </div>
      </SafDialog>
      {/* Pattern Removal Confirmation Dialog */}
      <SafDialog
        modal={false}
        is-alert={false}
        is-header={true}
        is-footer={true}
        no-focus-trap={false}
        dialog-title="Remove Selected Patterns"
        hidden={!removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
      >
        <div style={{ marginBottom: "16px" }}>
          Are you sure you want to remove the selected patterns? This action
          cannot be undone.
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <SafButton appearance="secondary" onClick={confirmPatternRemoval}>
            Yes, Remove
          </SafButton>
          <SafButton
            appearance="secondary"
            onClick={() => setRemoveDialogOpen(false)}
          >
            Cancel
          </SafButton>
        </div>
      </SafDialog>
      {openAlertOptions && (
        <SafAlert
          appearance={openAlertOptions[1]}
          alertType={openAlertOptions[0]}
          iconAriaLabel="icon aria label"
          closeAriaLabel="Close"
          onClose={handleOnClose}
        >
          {alertMessage}
        </SafAlert>
      )}
    </div>
  );
};

export default TechnologyStacks;
